import { useMemo } from 'react';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useAuth } from '@/hooks/useAuth';
import { useAccessResolver } from '@/contexts/AccessResolverContext';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Building, Clock, User, Crown, ArrowUpRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SmartHeaderProps {
  className?: string;
  showWorkspaceName?: boolean;
  showPlan?: boolean;
  showRole?: boolean;
  showTrialBadge?: boolean;
  sticky?: boolean;
}

/**
 * SmartHeader Component
 * 
 * A polished, professional header component that displays workspace information,
 * plan details, user role, and trial status. Designed with modern UI patterns
 * and proper alignment for global user expectations.
 * 
 * Features:
 * - Clean, modern design with proper spacing
 * - Responsive layout with mobile considerations
 * - Visual hierarchy with grouped information
 * - Color-coded status indicators
 * - Sticky positioning option
 * 
 * @example
 * <SmartHeader />
 * <SmartHeader showWorkspaceName={false} showTrialBadge={true} sticky />
 */
export const SmartHeader = ({
  className,
  showWorkspaceName = true,
  showPlan = true,
  showRole = true,
  showTrialBadge = true,
  sticky = false,
}: SmartHeaderProps) => {
  const { currentWorkspace } = useWorkspace();
  const { user } = useAuth();
  const { plan, resolution } = useAccessResolver();

  // Get plan information
  const planInfo = useMemo(() => {
    const planCode = plan?.code || currentWorkspace?.plan_code || 'trial';
    const planName = plan?.name || 
      (planCode === 'trial' ? 'Trial' : 
       planCode === 'starter' ? 'Starter' : 
       planCode === 'enterprise' ? 'Enterprise' : 
       planCode.charAt(0).toUpperCase() + planCode.slice(1).replace(/_/g, ' '));
    const isTrial = plan?.is_trial || planCode === 'trial' || planCode === 'free';

    return {
      code: planCode,
      name: planName,
      isTrial,
    };
  }, [plan, currentWorkspace?.plan_code]);

  // Calculate trial days remaining
  const trialInfo = useMemo(() => {
    if (!planInfo.isTrial) return null;

    // Try to get trial expiry from workspace
    const trialExpiresAt = 
      (currentWorkspace as any)?.trial_expires_at || 
      (currentWorkspace as any)?.trial_ends_at;

    if (!trialExpiresAt) return { isTrial: true, daysRemaining: null };

    const expiryDate = new Date(trialExpiresAt);
    const now = new Date();
    const diff = expiryDate.getTime() - now.getTime();
    const daysRemaining = Math.ceil(diff / (1000 * 60 * 60 * 24));

    return {
      isTrial: true,
      daysRemaining: daysRemaining > 0 ? daysRemaining : 0,
      isExpired: daysRemaining <= 0,
      isUrgent: daysRemaining !== null && daysRemaining <= 7 && daysRemaining > 0,
    };
  }, [planInfo.isTrial, currentWorkspace]);

  // Get user role display name
  const roleDisplayName = useMemo(() => {
    if (!user?.role) return 'Member';
    
    const roleMap: Record<string, string> = {
      owner: 'Owner',
      super_admin: 'Super Admin',
      workspace_admin: 'Workspace Admin',
      admin: 'Admin',
      member: 'Member',
      viewer: 'Viewer',
    };

    return roleMap[user.role] || user.role;
  }, [user?.role]);

  const isOwner = user?.role === 'owner' || user?.role === 'super_admin';

  if (!currentWorkspace) {
    return null;
  }

  return (
    <header
      className={cn(
        'h-14 border-b border-border/40',
        'bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60',
        'flex items-center justify-between',
        'px-4 sm:px-6',
        sticky && 'sticky top-0 z-50',
        className
      )}
    >
      {/* Left Section: Workspace Name */}
      <div className="flex items-center gap-3 flex-1 min-w-0">
        {showWorkspaceName && (
          <div className="flex items-center gap-2.5 min-w-0 group">
            <div className="flex items-center justify-center w-8 h-8 rounded-md bg-muted/50 group-hover:bg-muted transition-colors">
              <Building className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="min-w-0">
              <span 
                className="text-sm font-semibold text-foreground truncate block" 
                title={currentWorkspace.name}
              >
                {currentWorkspace.name}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Right Section: Status Badges */}
      <div className="flex items-center gap-2.5 flex-shrink-0">
        {/* Plan Badge */}
        {showPlan && planInfo.name && (
          <Badge 
            variant={
              planInfo.code === 'enterprise' ? 'default' :
              planInfo.code === 'starter' ? 'secondary' :
              'outline'
            }
            className="text-xs font-medium px-2.5 py-1 h-7 border-border/60 hover:bg-muted/50 transition-colors"
          >
            {planInfo.name}
          </Badge>
        )}

        {/* Upgrade Button - Trial Only */}
        {planInfo.isTrial && (
          <Button
            size="sm"
            variant="default"
            className="text-xs font-medium px-3 py-1 h-7 flex items-center gap-1.5"
            onClick={() => {
              // Navigate to billing/upgrade page
              if (currentWorkspace?.slug) {
                window.location.href = `/${currentWorkspace.slug}/billing`;
              }
            }}
          >
            <span>Upgrade</span>
            <ArrowUpRight className="h-3 w-3" />
          </Button>
        )}

        {/* Separator */}
        {showPlan && (showRole || showTrialBadge || isOwner) && (
          <Separator orientation="vertical" className="h-5" />
        )}

        {/* Trial Expiry Badge - Priority Display */}
        {showTrialBadge && trialInfo?.isTrial && (
          <Badge
            variant={
              trialInfo.isExpired 
                ? 'destructive' 
                : trialInfo.isUrgent 
                ? 'destructive' 
                : 'default'
            }
            className={cn(
              'text-xs font-medium px-2.5 py-1 h-7 flex items-center gap-1.5',
              'transition-all hover:scale-105',
              trialInfo.isUrgent && 'animate-pulse'
            )}
          >
            <Clock className="h-3.5 w-3.5" />
            <span>
              {trialInfo.isExpired
                ? 'Trial Expired'
                : trialInfo.daysRemaining !== null
                ? `${trialInfo.daysRemaining} ${trialInfo.daysRemaining === 1 ? 'day' : 'days'} left`
                : 'Trial Active'}
            </span>
          </Badge>
        )}

        {/* Owner Badge - High Priority */}
        {isOwner && (
          <Badge 
            variant="default" 
            className="text-xs font-medium px-2.5 py-1 h-7 flex items-center gap-1.5 bg-primary/10 text-primary border-primary/20 hover:bg-primary/20 transition-colors"
          >
            <Crown className="h-3.5 w-3.5" />
            <span>Owner</span>
          </Badge>
        )}

        {/* User Role Badge */}
        {showRole && user && !isOwner && (
          <>
            {(showTrialBadge && trialInfo?.isTrial) && (
              <Separator orientation="vertical" className="h-5" />
            )}
            <Badge 
              variant="secondary" 
              className="text-xs font-medium px-2.5 py-1 h-7 flex items-center gap-1.5 hover:bg-secondary/80 transition-colors"
            >
              <User className="h-3.5 w-3.5" />
              <span>{roleDisplayName}</span>
            </Badge>
          </>
        )}
      </div>
    </header>
  );
};
